'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { ArrowRight, BarChart3, Heart, MapPin } from 'lucide-react';
interface CTACardProps {
  icon: React.ReactNode;
  badge: string;
  badgeColor: string;
  title: string;
  description: string;
  href: string;
  buttonLabel: string;
  accentFrom: string;
  accentTo: string;
}
function CTACard(t0) {
  const $ = _c(18);
  const {
    icon,
    badge,
    badgeColor,
    title,
    description,
    href,
    buttonLabel,
    accentFrom,
    accentTo
  } = t0;
  const t1 = `inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-5 w-fit ${badgeColor}`;
  let t2;
  if ($[0] !== badge || $[1] !== icon || $[2] !== t1) {
    t2 = <div className={t1}>{icon}{badge}</div>;
    $[0] = badge;
    $[1] = icon;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== title) {
    t3 = <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">{title}</h3>;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== description) {
    t4 = <p className="text-gray-400 text-sm leading-relaxed mb-8 flex-1">{description}</p>;
    $[6] = description;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = `inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r ${accentFrom} ${accentTo} text-white font-semibold rounded-full hover:opacity-90 transition-all cursor-pointer text-sm w-full`;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ArrowRight className="h-4 w-4" />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== buttonLabel || $[10] !== href || $[11] !== t5) {
    t7 = <Link href={href} className={t5}>{buttonLabel}{t6}</Link>;
    $[9] = buttonLabel;
    $[10] = href;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t7) {
    t8 = <div className="group relative bg-gray-800 rounded-2xl p-8 hover:bg-gray-750 transition-all duration-300 flex flex-col">{t2}{t3}{t4}{t7}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
export function FinalCTASection() {
  const $ = _c(6);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="absolute top-0 right-0 w-80 h-80 bg-primary-600/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />;
    t1 = <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="text-center mb-12"><h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4">Let's go!</h2></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CTACard icon={<BarChart3 className="h-3.5 w-3.5" />} badge="Brands & Trade" badgeColor="bg-purple-500/20 text-purple-300" title="Launch activations and connect with buyers" description="Get your products into the hands of real consumers and retail buyers. Run sampling campaigns, manage trade relationships, and measure what works." href="/opportunities" buttonLabel="Opportunities" accentFrom="from-purple-600" accentTo="to-purple-500" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CTACard icon={<Heart className="h-3.5 w-3.5" />} badge="For Me" badgeColor="bg-primary-500/20 text-primary-300" title="Discover and try products you'll love" description="Find new food and beverage products, join tasting events, take quizzes, and get rewarded for sharing what you think." href="/for-me" buttonLabel="Discover and Try" accentFrom="from-primary-600" accentTo="to-primary-500" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <section className="py-20 lg:py-28"><div className="container mx-auto px-4 sm:px-6 lg:px-8"><div className="bg-gray-900 rounded-3xl p-10 md:p-16 relative overflow-hidden">{t0}{t1}<div className="relative z-10">{t2}<div className="grid grid-cols-1 md:grid-cols-3 gap-6">{t3}{t4}<CTACard icon={<MapPin className="h-3.5 w-3.5" />} badge="Host" badgeColor="bg-cyan-500/20 text-cyan-300" title="Be part of the action at your place" description={"Whether you're a venue, workplace, university, or event space \u2014 offer your location for sampling, pop-ups, and brand activations. Give your people something fun and get matched with brands looking for your audience."} href="/host-opportunities" buttonLabel="Host" accentFrom="from-cyan-600" accentTo="to-cyan-500" /></div></div></div></div></section>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}